export type CvssSecurityRequirementValue = 'X' | 'L' | 'M' | 'H';

export type CvssModifiedAttackVectorValue = 'X' | 'N' | 'A' | 'L' | 'P';
export type CvssModifiedAttackComplexityValue = 'X' | 'L' | 'H';
export type CvssModifiedAttackRequirementsValue = 'X' | 'N' | 'P';
export type CvssModifiedPrivilegesRequiredValue = 'X' | 'N' | 'L' | 'H';
export type CvssModifiedUserInteractionValue = 'X' | 'N' | 'R' | 'P' | 'A';
export type CvssModifiedScopeValue = 'X' | 'U' | 'C';
export type CvssModifiedImpactValue = 'X' | 'N' | 'L' | 'H';
export type CvssModifiedSubsequentImpactValue = 'X' | 'N' | 'L' | 'H' | 'S';

export type CvssModifiedBaseMetrics = {
  mav: CvssModifiedAttackVectorValue;
  mac: CvssModifiedAttackComplexityValue;
  mat: CvssModifiedAttackRequirementsValue;
  mpr: CvssModifiedPrivilegesRequiredValue;
  mui: CvssModifiedUserInteractionValue;
  ms: CvssModifiedScopeValue;
  mc: CvssModifiedImpactValue;
  mi: CvssModifiedImpactValue;
  ma: CvssModifiedImpactValue;
  mvc: CvssModifiedImpactValue;
  mvi: CvssModifiedImpactValue;
  mva: CvssModifiedImpactValue;
  msc: CvssModifiedImpactValue;
  msi: CvssModifiedSubsequentImpactValue;
  msa: CvssModifiedSubsequentImpactValue;
};

export type CvssSecurityRequirementMetricKey = 'cr' | 'ir' | 'ar';

export type CvssEnvironmentalRequirements = {
  cr: CvssSecurityRequirementValue;
  ir: CvssSecurityRequirementValue;
  ar: CvssSecurityRequirementValue;
} & CvssModifiedBaseMetrics;

export type CvssEnvironmentalScoreResult = {
  baseScore: number;
  environmentalScore: number;
  environmentalVector: string | null;
  effectiveScore: number;
  isEnvironmentalConfigured: boolean;
};

export const CVSS_SECURITY_REQUIREMENT_VALUES = [
  'X',
  'L',
  'M',
  'H',
] as const satisfies readonly CvssSecurityRequirementValue[];

export const CVSS_MODIFIED_ATTACK_VECTOR_VALUES = [
  'X',
  'N',
  'A',
  'L',
  'P',
] as const satisfies readonly CvssModifiedAttackVectorValue[];

export const CVSS_MODIFIED_ATTACK_COMPLEXITY_VALUES = [
  'X',
  'L',
  'H',
] as const satisfies readonly CvssModifiedAttackComplexityValue[];

export const CVSS_MODIFIED_ATTACK_REQUIREMENTS_VALUES = [
  'X',
  'N',
  'P',
] as const satisfies readonly CvssModifiedAttackRequirementsValue[];

export const CVSS_MODIFIED_PRIVILEGES_REQUIRED_VALUES = [
  'X',
  'N',
  'L',
  'H',
] as const satisfies readonly CvssModifiedPrivilegesRequiredValue[];

export const CVSS_31_MODIFIED_USER_INTERACTION_VALUES = [
  'X',
  'N',
  'R',
] as const satisfies readonly Extract<
  CvssModifiedUserInteractionValue,
  'X' | 'N' | 'R'
>[];

export const CVSS_40_MODIFIED_USER_INTERACTION_VALUES = [
  'X',
  'N',
  'P',
  'A',
] as const satisfies readonly Extract<
  CvssModifiedUserInteractionValue,
  'X' | 'N' | 'P' | 'A'
>[];

export const CVSS_MODIFIED_USER_INTERACTION_VALUES = [
  'X',
  'N',
  'R',
  'P',
  'A',
] as const satisfies readonly CvssModifiedUserInteractionValue[];

export const CVSS_MODIFIED_SCOPE_VALUES = [
  'X',
  'U',
  'C',
] as const satisfies readonly CvssModifiedScopeValue[];

export const CVSS_MODIFIED_IMPACT_VALUES = [
  'X',
  'N',
  'L',
  'H',
] as const satisfies readonly CvssModifiedImpactValue[];

export const CVSS_MODIFIED_SUBSEQUENT_IMPACT_VALUES = [
  'X',
  'N',
  'L',
  'H',
  'S',
] as const satisfies readonly CvssModifiedSubsequentImpactValue[];

export type Cvss31ModifiedMetricKey =
  | 'mav'
  | 'mac'
  | 'mpr'
  | 'mui'
  | 'ms'
  | 'mc'
  | 'mi'
  | 'ma';

export type Cvss40ModifiedMetricKey =
  | 'mav'
  | 'mac'
  | 'mat'
  | 'mpr'
  | 'mui'
  | 'mvc'
  | 'mvi'
  | 'mva'
  | 'msc'
  | 'msi'
  | 'msa';

export const CVSS_31_MODIFIED_METRIC_ALLOWED_VALUES = {
  mav: CVSS_MODIFIED_ATTACK_VECTOR_VALUES,
  mac: CVSS_MODIFIED_ATTACK_COMPLEXITY_VALUES,
  mpr: CVSS_MODIFIED_PRIVILEGES_REQUIRED_VALUES,
  mui: CVSS_31_MODIFIED_USER_INTERACTION_VALUES,
  ms: CVSS_MODIFIED_SCOPE_VALUES,
  mc: CVSS_MODIFIED_IMPACT_VALUES,
  mi: CVSS_MODIFIED_IMPACT_VALUES,
  ma: CVSS_MODIFIED_IMPACT_VALUES,
} as const satisfies {
  [K in Cvss31ModifiedMetricKey]: readonly CvssModifiedBaseMetrics[K][];
};

export const CVSS_40_MODIFIED_METRIC_ALLOWED_VALUES = {
  mav: CVSS_MODIFIED_ATTACK_VECTOR_VALUES,
  mac: CVSS_MODIFIED_ATTACK_COMPLEXITY_VALUES,
  mat: CVSS_MODIFIED_ATTACK_REQUIREMENTS_VALUES,
  mpr: CVSS_MODIFIED_PRIVILEGES_REQUIRED_VALUES,
  mui: CVSS_40_MODIFIED_USER_INTERACTION_VALUES,
  mvc: CVSS_MODIFIED_IMPACT_VALUES,
  mvi: CVSS_MODIFIED_IMPACT_VALUES,
  mva: CVSS_MODIFIED_IMPACT_VALUES,
  msc: CVSS_MODIFIED_IMPACT_VALUES,
  msi: CVSS_MODIFIED_SUBSEQUENT_IMPACT_VALUES,
  msa: CVSS_MODIFIED_SUBSEQUENT_IMPACT_VALUES,
} as const satisfies {
  [K in Cvss40ModifiedMetricKey]: readonly CvssModifiedBaseMetrics[K][];
};

export const CVSS_MODIFIED_METRIC_ALLOWED_VALUES = {
  mav: CVSS_MODIFIED_ATTACK_VECTOR_VALUES,
  mac: CVSS_MODIFIED_ATTACK_COMPLEXITY_VALUES,
  mat: CVSS_MODIFIED_ATTACK_REQUIREMENTS_VALUES,
  mpr: CVSS_MODIFIED_PRIVILEGES_REQUIRED_VALUES,
  mui: CVSS_MODIFIED_USER_INTERACTION_VALUES,
  ms: CVSS_MODIFIED_SCOPE_VALUES,
  mc: CVSS_MODIFIED_IMPACT_VALUES,
  mi: CVSS_MODIFIED_IMPACT_VALUES,
  ma: CVSS_MODIFIED_IMPACT_VALUES,
  mvc: CVSS_MODIFIED_IMPACT_VALUES,
  mvi: CVSS_MODIFIED_IMPACT_VALUES,
  mva: CVSS_MODIFIED_IMPACT_VALUES,
  msc: CVSS_MODIFIED_IMPACT_VALUES,
  msi: CVSS_MODIFIED_SUBSEQUENT_IMPACT_VALUES,
  msa: CVSS_MODIFIED_SUBSEQUENT_IMPACT_VALUES,
} as const satisfies {
  [K in keyof CvssModifiedBaseMetrics]: readonly CvssModifiedBaseMetrics[K][];
};

export const CVSS_31_MODIFIED_BASE_METRIC_KEYS = [
  'mav',
  'mac',
  'mpr',
  'mui',
  'ms',
  'mc',
  'mi',
  'ma',
] as const satisfies readonly Cvss31ModifiedMetricKey[];

export const CVSS_40_MODIFIED_BASE_METRIC_KEYS = [
  'mav',
  'mac',
  'mat',
  'mpr',
  'mui',
  'mvc',
  'mvi',
  'mva',
  'msc',
  'msi',
  'msa',
] as const satisfies readonly Cvss40ModifiedMetricKey[];

export const DEFAULT_CVSS_MODIFIED_BASE_METRICS: CvssModifiedBaseMetrics = {
  mav: 'X',
  mac: 'X',
  mat: 'X',
  mpr: 'X',
  mui: 'X',
  ms: 'X',
  mc: 'X',
  mi: 'X',
  ma: 'X',
  mvc: 'X',
  mvi: 'X',
  mva: 'X',
  msc: 'X',
  msi: 'X',
  msa: 'X',
};

export const DEFAULT_CVSS_ENVIRONMENTAL_REQUIREMENTS: CvssEnvironmentalRequirements =
  {
    cr: 'X',
    ir: 'X',
    ar: 'X',
    ...DEFAULT_CVSS_MODIFIED_BASE_METRICS,
  };

export const CVSS_SECURITY_REQUIREMENT_METRIC_KEYS = [
  'cr',
  'ir',
  'ar',
] as const satisfies readonly CvssSecurityRequirementMetricKey[];

export const CVSS_SECURITY_REQUIREMENT_VECTOR_KEYS: Record<
  CvssSecurityRequirementMetricKey,
  string
> = {
  cr: 'CR',
  ir: 'IR',
  ar: 'AR',
};

export const CVSS_MODIFIED_BASE_METRIC_KEYS = [
  'mav',
  'mac',
  'mat',
  'mpr',
  'mui',
  'ms',
  'mc',
  'mi',
  'ma',
  'mvc',
  'mvi',
  'mva',
  'msc',
  'msi',
  'msa',
] as const satisfies readonly (keyof CvssModifiedBaseMetrics)[];

export const CVSS_MODIFIED_BASE_VECTOR_KEYS: Record<
  keyof CvssModifiedBaseMetrics,
  string
> = {
  mav: 'MAV',
  mac: 'MAC',
  mat: 'MAT',
  mpr: 'MPR',
  mui: 'MUI',
  ms: 'MS',
  mc: 'MC',
  mi: 'MI',
  ma: 'MA',
  mvc: 'MVC',
  mvi: 'MVI',
  mva: 'MVA',
  msc: 'MSC',
  msi: 'MSI',
  msa: 'MSA',
};

export const isCvssSecurityRequirementValue = (
  value: string,
): value is CvssSecurityRequirementValue =>
  (CVSS_SECURITY_REQUIREMENT_VALUES as readonly string[]).includes(value);

export const resolveModifiedMetricValue = <
  K extends keyof CvssModifiedBaseMetrics,
>({
  key,
  value,
}: {
  key: K;
  value: string | null | undefined;
}): CvssModifiedBaseMetrics[K] => {
  const allowed = CVSS_MODIFIED_METRIC_ALLOWED_VALUES[key] as readonly string[];
  if (value && allowed.includes(value)) {
    return value as CvssModifiedBaseMetrics[K];
  }
  return 'X' as CvssModifiedBaseMetrics[K];
};
